import {
    AppBar,
    Box,
    Button,
    Divider,
    IconButton,
    Link,
    Stack,
    Toolbar
} from '@mui/material'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import BrandLogo from '@/components/BrandLogo'
import { useBranding } from '@/contexts/BrandingContext'
import MenuIcon from '@/icons/Menu'
import { isBetaMode as isBeta } from '@/lib/betaMode'
import {
    getVisibleSegments,
    showsPlatformPricing
} from '@/pages/marketing/segments/registry.js'
import FeaturesMenu from './FeaturesMenu'

const linkSx = { '&:hover': { color: 'primary.main' } }

const MainNavbar = (props) => {
    const { onSidebarMobileOpen } = props
    const { brand } = useBranding()
    const isPartnerHost = Boolean(brand?.is_partner)

    // On a partner's white-label host the partner segment and platform pricing are both hidden
    // — we do not recruit resellers or quote our own prices in front of a partner's clients.
    const segments = getVisibleSegments(isPartnerHost, brand?.brand_key)
    // `&& !isBeta` because a beta build does not MOUNT /pricing (buildPublicRoutes
    // returns nothing), so advertising it here rendered a link straight to the
    // catch-all 404. Visibility and mounting now read the same flag.
    const showPricing = showsPlatformPricing(isPartnerHost) && !isBeta

    return (
        <AppBar
            elevation={0}
            sx={{
                backgroundColor: 'background.paper',
                color: 'text.secondary',
                // viewport-fit=cover: keep the bar's content out of the notch
                // on a landscape iPhone.
                pl: 'env(safe-area-inset-left, 0px)',
                pr: 'env(safe-area-inset-right, 0px)'
            }}
        >
            <Toolbar sx={{ minHeight: 64 }}>
                <IconButton
                    color="inherit"
                    onClick={onSidebarMobileOpen}
                    sx={{ display: { md: 'none' }, mr: 1 }}
                >
                    <MenuIcon fontSize="small" />
                </IconButton>

                <Box
                    component={RouterLink}
                    to="/"
                    sx={{
                        alignItems: 'center',
                        color: 'text.primary',
                        display: 'flex',
                        textDecoration: 'none'
                    }}
                >
                    <BrandLogo />
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                {/* Desktop links. The mobile sidebar carries the same set — keep them in step. */}
                <Stack
                    direction="row"
                    spacing={2.5}
                    sx={{
                        alignItems: 'center',
                        display: { xs: 'none', md: 'flex' },
                        mr: 3
                    }}
                >
                    {/* Product before audience: what it does, then who it is for. */}
                    <FeaturesMenu />
                    {segments.map((segment) => (
                        <Link
                            key={segment.key}
                            color="textSecondary"
                            component={RouterLink}
                            sx={linkSx}
                            to={segment.path}
                            underline="none"
                            variant="body2"
                        >
                            {segment.chooserTitle}
                        </Link>
                    ))}
                    {showPricing && (
                        <Link
                            color="textSecondary"
                            component={RouterLink}
                            sx={linkSx}
                            to="/pricing"
                            underline="none"
                            variant="body2"
                        >
                            Pricing
                        </Link>
                    )}
                    <Link
                        color="textSecondary"
                        component={RouterLink}
                        sx={linkSx}
                        to="/contact"
                        underline="none"
                        variant="body2"
                    >
                        Contact
                    </Link>
                </Stack>

                <Button
                    color="primary"
                    component={RouterLink}
                    size="small"
                    to="/login"
                    variant="contained"
                >
                    Sign in
                </Button>
            </Toolbar>
            <Divider />
        </AppBar>
    )
}

MainNavbar.propTypes = {
    onSidebarMobileOpen: PropTypes.func
}

export default MainNavbar
